import { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { currentIsSubstep } from "../components/TemplateInterpreter/utils";
import { useNavigate } from "react-router-dom";
import {
  onUpdateCompletedForm as onUpdateCompletedFormOnboarding,
  onUpdateCompletedStep as onUpdateCompletedStepOnboarding,
  onUpdateDisabledSubStep,
  onUpdateFormData as onUpdateFormDataOnboarding,
  onUpdateLastCompletedSubStep as onUpdateLastCompletedSubStepOnboarding,
  onUpdateReachedStep as onUpdateReachedStepOnboarding,
  onUpdateStep as onUpdateStepOnboarding,
  onUpdateSubStep as onUpdateSubStepOnboarding,
} from "../reducers/onboardingDataReducer";
import {
  onSaveCurrentHolder,
  onUpdateCompletedForm as onUpdateCompletedFormSecurities,
  onUpdateCompletedStep as onUpdateCompletedStepSecurities,
  onUpdateFormData as onUpdateFormDataSecurities,
  onUpdateLastCompletedSubStep as onUpdateLastCompletedSubStepSecurities,
  onUpdateReachedStep as onUpdateReachedStepSecurities,
  onUpdateStep as onUpdateStepSecurities,
  onUpdateSubStep as onUpdateSubStepSecurities,
} from "../reducers/securitiesReducer";
import {
  utilsForLanguage,
  isBasicStep,
  isUnifiedOnboarding,
  isAllariaMasOnboarding,
  checkUserInBlacklist,
} from "../utils";
import { onUpdateActiveTemplate } from "../reducers/activeTemplateReducer";
import { showToastMessage } from "@almafintech/react-components/ToastMessage";
import { FormikProps, FormikValues } from "formik";
import { loadingOnContinue } from "../reducers/buttonsReducer";
import * as Sentry from "@sentry/react";
import { fileToBase64, showCustomToastifyError } from "../app/utils";
import { onUpdateIsFromNormalFlow } from "../reducers/preconfirmationReducer";
import { onUpdateInfoStep } from "../reducers/infoStepReducer";

const useForm = (formik?: FormikProps<FormikValues>) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const state = useAppSelector((state) => state);

  const { values, setValues, setFieldValue } = formik || {};

  const {
    activeTemplate: template,
    onboardingData,
    securities,
    preconfirmation: { isEditingForm },
  } = state;

  const language = state?.securities?.language ?? "es";

  const {
    onUpdateCompletedForm,
    onUpdateCompletedStep,
    onUpdateFormData,
    onUpdateLastCompletedSubStep,
    onUpdateReachedStep,
    onUpdateStep,
    onUpdateSubStep,
  } = {
    onUpdateCompletedForm:
      template?.onboardingType === "SECURITIES"
        ? onUpdateCompletedFormSecurities
        : onUpdateCompletedFormOnboarding,
    onUpdateCompletedStep:
      template?.onboardingType === "SECURITIES"
        ? onUpdateCompletedStepSecurities
        : onUpdateCompletedStepOnboarding,
    onUpdateFormData:
      template?.onboardingType === "SECURITIES"
        ? onUpdateFormDataSecurities
        : onUpdateFormDataOnboarding,
    onUpdateLastCompletedSubStep:
      template?.onboardingType === "SECURITIES"
        ? onUpdateLastCompletedSubStepSecurities
        : onUpdateLastCompletedSubStepOnboarding,
    onUpdateReachedStep:
      template?.onboardingType === "SECURITIES"
        ? onUpdateReachedStepSecurities
        : onUpdateReachedStepOnboarding,
    onUpdateStep:
      template?.onboardingType === "SECURITIES"
        ? onUpdateStepSecurities
        : onUpdateStepOnboarding,
    onUpdateSubStep:
      template?.onboardingType === "SECURITIES"
        ? onUpdateSubStepSecurities
        : onUpdateSubStepOnboarding,
  };

  const {
    stepData: { step, subStep },
  } = template.onboardingType === "SECURITIES" ? securities : onboardingData;

  // Get current step based on main step
  const currentStep = useMemo(
    () => template?.steps?.find((s) => s.step === step),
    [step, template?.steps]
  );

  // Check if current step has substeps
  const currentIsSubStep = currentIsSubstep(currentStep);

  // Get numeric template steps
  const numericTemplateSteps = template?.steps?.filter(
    ({ step: templateStep }) => typeof templateStep === "number"
  );

  // Check if current step is the last step
  const isLastStep = useMemo(() => {
    return (
      step ===
      template?.steps?.filter(({ step }) => typeof step === "number").length
    );
  }, [template?.steps, step]);

  // Check if current step is the last step and substep
  const isLastStepAndSubStep = useMemo(() => {
    return (
      step ===
        template?.steps?.filter(({ step }) => typeof step === "number")
          .length &&
      (currentIsSubStep ? subStep === currentStep?.subSteps?.length : true)
    );
  }, [template?.steps, currentIsSubStep, currentStep, step, subStep]);

  // Unify steps and substeps
  const currentStepData = useMemo(() => {
    if (!isBasicStep(currentStep)) return currentStep;

    if ("subSteps" in currentStep) {
      return currentStep?.subSteps?.find((s) => s.step === (subStep || 1));
    } else {
      return currentStep;
    }
  }, [currentStep, subStep]);

  // Find previous step based on current step
  const findPreviousStep = () => {
    if (!numericTemplateSteps || !currentStep?.step) return null;
    const position = Number(currentStep.step) - 1;

    // Iterate backwards from the current step
    for (let index = position; index > 0; index--) {
      const element = numericTemplateSteps.find((s) => s.step === index);
      if (element)
        if (element.step === 1)
          return true; //This mean that the current step is TOKEN and we can go back
        else if (!element.disableBackNavigation) return element?.step;
    }

    return null;
  };

  // Handle Back Button
  const handleBack = async () => {
    if (!currentStepData || !values || !setValues) return;

    if (currentStep?.step === 1) navigate("/");

    if ("onBack" in currentStepData) {
      const res = await currentStepData.onBack?.(values, dispatch, state);

      if (res?.backStep) {
        dispatch(onUpdateStep(res.backStep));
      }

      if (res?.backSubStep) {
        dispatch(onUpdateSubStep(res.backSubStep));
      }
      return;
    }

    if (currentIsSubStep) {
      if (Number(subStep) > 1) {
        dispatch(onUpdateSubStep(Number(subStep) - 1));
      } else {
        const previousStep = findPreviousStep();
        if (previousStep) {
          dispatch(onUpdateStep(Number(previousStep)));
        }
      }
    } else {
      const previousStep = findPreviousStep();
      if (previousStep) dispatch(onUpdateStep(Number(previousStep)));
    }
  };

  // Handle Continue Button
  const handleContinue = async () => {
    if (!currentStepData || !values || !setValues) return;

    // Process form values to convert File objects to base64 strings
    const processFormValues = async (obj: unknown): Promise<unknown> => {
      if (obj === null || obj === undefined) {
        return obj;
      }

      if (obj instanceof File) {
        const base64String = await fileToBase64(obj);

        return {
          base64String,
          name: obj.name,
          type: obj.type,
        };
      }

      if (Array.isArray(obj)) {
        return Promise.all(obj.map((item) => processFormValues(item)));
      }

      if (typeof obj === "object" && !(obj instanceof Date)) {
        const result: Record<string, unknown> = {};

        for (const key in obj as Record<string, unknown>) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
            result[key] = await processFormValues(
              (obj as Record<string, unknown>)[key]
            );
          }
        }
        return result;
      }

      return obj;
    };

    const processedValues = await processFormValues(values);

    dispatch(onUpdateFormData(processedValues as FormikValues));

    const isLastSubstep =
      Number(subStep) === Number(currentStep?.subSteps?.length);

    const continueToPreconfirmation = () => {
      dispatch(
        onUpdateActiveTemplate({
          onboardingType: template?.onboardingType,
          preconfirmation: template?.preconfirmation,
        })
      );
      dispatch(onUpdateCompletedForm(true));

      //If is unified onboarding, keep parameters in the URL
      if (isUnifiedOnboarding() && isAllariaMasOnboarding()) {
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set("template", template?.onboardingType || "");
        dispatch(onUpdateIsFromNormalFlow(true));
        navigate(`/preconfirmacion?${currentParams.toString()}`);
      } else {
        dispatch(onUpdateIsFromNormalFlow(true));
        navigate(`/preconfirmacion?template=${template?.onboardingType}`);
      }

      if (template.onboardingType === "SECURITIES") {
        dispatch(onSaveCurrentHolder());
      }
      return;
    };

    if ("onContinue" in currentStepData) {
      dispatch(loadingOnContinue(true));
      try {
        if (!currentStepData?.onContinue) return;

        const isInBlacklist = await checkUserInBlacklist(
          values.email,
          values.cuit
        );
        if (isInBlacklist && template?.onboardingType !== "SECURITIES") {
          dispatch(onUpdateInfoStep("BLACKLIST"));
          dispatch(onUpdateStep("information-step"));
          return;
        }

        const res = await currentStepData?.onContinue(
          values,
          dispatch,
          state,
          setFieldValue
        );

        if (!currentIsSubStep || isLastSubstep) {
          dispatch(onUpdateCompletedStep(Number(step)));
        }

        if (res?.nextStep) {
          dispatch(onUpdateReachedStep(Number(res.nextStep)));
          return dispatch(onUpdateStep(res.nextStep));
        }

        if (res?.newValuesToForm) {
          setValues((prevValues) => ({
            ...prevValues,
            ...res.newValuesToForm,
          }));
        }

        if (res?.continueToPreconfirmation) {
          return continueToPreconfirmation();
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.log(error);
        if (state?.onboardingData?.draftUUID)
          Sentry.setTag("uuid", state.onboardingData?.draftUUID);
        if (error && typeof error === "object" && "response" in error) {
          Sentry.captureException(error.response.data);
          if (
            ["CXU_NOT_OWNER", "CXU_NOT_FOUND"].includes(
              error.response.data?.code
            )
          )
            showCustomToastifyError(error.response.data.message);
          else if (error.response.data?.code === "DRAFT_NOT_FOUND") {
            dispatch(onUpdateInfoStep("DRAFT_NOT_FOUND"));
            dispatch(onUpdateStep("information-step"));
          } else
            showToastMessage(utilsForLanguage(language).genericToastifyError, {
              containerId: "formAlert",
              type: "error",
            });
        } else {
          Sentry.captureException(error);
          if (["DNI_NO_VIGENTE", "INVALID_DATA_DNI"].includes(error.message))
            showCustomToastifyError(error.message);
          else
            showToastMessage(utilsForLanguage(language).genericToastifyError, {
              containerId: "formAlert",
              type: "error",
            });
        }
        if (!currentIsSubStep || isLastSubstep) {
          dispatch(onUpdateCompletedStep(Number(step) - 1));
        }
        if (isLastSubstep) {
          dispatch(onUpdateLastCompletedSubStep(Number(subStep) - 1));
        }
        return;
      } finally {
        dispatch(loadingOnContinue(false));
      }
    }

    if (currentIsSubStep) {
      if (!isLastSubstep) {
        dispatch(onUpdateLastCompletedSubStep(Number(subStep)));
        dispatch(onUpdateSubStep(Number(subStep) + 1));
      } else {
        if (isEditingForm && template?.onboardingType !== "JURIDICAL") {
          continueToPreconfirmation();
        } else {
          dispatch(onUpdateCompletedStep(Number(step)));
          dispatch(onUpdateDisabledSubStep(null));

          if (!isLastStep) {
            const nextStep = Number(step) + 1;
            dispatch(onUpdateStep(nextStep));
            dispatch(onUpdateReachedStep(nextStep));
            dispatch(onUpdateSubStep(1));
            dispatch(onUpdateLastCompletedSubStep(0));
          }
        }
      }
    } else {
      if (isEditingForm && template?.onboardingType !== "JURIDICAL") {
        continueToPreconfirmation();
      } else {
        if (!isLastStep) {
          const nextStep = Number(step) + 1;
          dispatch(onUpdateStep(nextStep));
          dispatch(onUpdateReachedStep(nextStep));
          dispatch(onUpdateSubStep(1));
        }
        dispatch(onUpdateDisabledSubStep(null));
      }
    }

    if (isLastStepAndSubStep) {
      if (template.preconfirmation) continueToPreconfirmation();

      template.onTemplateCompleted?.({ values, state, dispatch, navigate });
    }
  };

  return {
    currentStep,
    currentIsSubStep,
    numericTemplateSteps,
    isLastStep,
    currentStepData,
    findPreviousStep,
    handleBack,
    handleContinue,
  };
};

export default useForm;
